/**
 * The single source of truth for "is this BI result cell numeric?" and "what
 * number does it plot as?", shared by the compute-time widget validator
 * (`apps/api` BI widget validation) and every frontend chart renderer.
 *
 * Why this lives in one place: the validator's contract promises that a result
 * which passes validation renders correctly ("previewed fine but rejected at
 * render time is structurally impossible"). That guarantee only holds if the
 * validator and the renderers agree, cell-for-cell, on what counts as numeric.
 * When they each carried their own predicate they drifted — the validator
 * accepted numeric strings (correct: Postgres returns `numeric`/`int8` as
 * strings) while the renderers required a JS `number`, so a fully-valid result
 * rendered as a flat 0 (STK-255). Consuming these two functions on both sides
 * makes that disagreement impossible by construction (STK-267).
 */

/**
 * A cell counts as numeric if it's a JS number or a numeric string (Postgres
 * returns `numeric`/`int8` as strings) — or `null` (a plottable gap). Uses
 * `!Number.isNaN` rather than `Number.isFinite` so a large-magnitude aggregate
 * (a Postgres NUMERIC beyond JS float range parses to `Infinity`) still counts
 * as numeric. An empty/whitespace string is NOT numeric (`Number("")` is 0).
 *
 * NOTE: `null` counts as numeric (a gap), so a column that is ENTIRELY null
 * passes here — callers that must plot a series additionally require at least
 * one non-null cell.
 */
export function isNumericCell(value: unknown): boolean {
  if (value === null) return true
  if (typeof value === "number") return !Number.isNaN(value)
  if (typeof value === "string") {
    const t = value.trim()
    return t !== "" && !Number.isNaN(Number(t))
  }
  return false
}

/**
 * Coerce a result cell to the number it plots as, consistent with
 * `isNumericCell` by construction: for every cell the predicate accepts as a
 * non-null number this returns that exact value (parsing the pg numeric
 * string); `null`, `undefined`, and non-numeric cells collapse to 0. A renderer
 * that wants a null gap instead of 0 should branch on `isNumericCell` itself.
 *
 * Invariant (unit-tested): for any `v` where `isNumericCell(v)` is true and
 * `v !== null`, `toChartNumber(v) === Number(v)`; otherwise `toChartNumber(v)`
 * is 0.
 */
export function toChartNumber(value: unknown): number {
  if (value === null || value === undefined) return 0
  if (!isNumericCell(value)) return 0
  const n = typeof value === "number" ? value : Number(value)
  return Number.isNaN(n) ? 0 : n
}
